import type { ToastOptions } from '@common/hooks';
import { getSubDomain } from '@core/utils';
import * as Sentry from '@sentry/nextjs';
import { useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { AuthApi } from '@/api';
import { COOKIES_KEYS, SESSION_KEYS_CLEARED_ON_LOGOUT, SESSION_KEYS_CLEARED_ON_USER_LOGOUT, STORAGE_KEY, SUB_DOMAIN } from '@/constants';
import { isAppLaunchSettled } from '@/stores/AppLaunchStore';
import { useAuthStore } from '@/stores/AuthStore';
import { switchMyPageUser } from '@/stores/useMyPageStore';
import { cookies } from '@/utils/cookies';
import { useAccessToken } from './useAccessToken';
interface LogoutOptions {
  redirect?: string;
  toastOptions?: ToastOptions;
}

//* 정리 대상은 storage-key.ts가 관리한다 — 호출부 나열 방식은 신규 키를 계속 누락시켰다
const clearStoredSession = () => SESSION_KEYS_CLEARED_ON_LOGOUT.forEach(key => sessionStorage.removeItem(key));
const stashLogoutToast = (toastOptions?: ToastOptions) => {
  if (!toastOptions) {
    return;
  }
  sessionStorage.setItem(STORAGE_KEY.LOGOUT_TOAST, JSON.stringify(toastOptions));
};
export const useLogout = () => {
  const {
    setAccessToken,
    setRefreshToken
  } = useAccessToken();
  const [setMyInfo, setIsDisabledAccessControl] = useAuthStore(useShallow(state => [state.setMyInfo, state.setIsDisabledAccessControl]));
  const queryClient = useQueryClient();
  const clearCredentials = () => {
    //* 쿼리 키에 사용자 구분이 없다 — 세션이 있었을 때만 비워 비로그인 화면마다 도는 clearSession이 캐시를 날리지 않게 한다
    if (cookies.get(COOKIES_KEYS.ACCESS_TOKEN) || cookies.get(COOKIES_KEYS.REFRESH_TOKEN)) {
      queryClient.removeQueries();
    }
    setAccessToken(null);
    setRefreshToken(null);
    setMyInfo(null);
    switchMyPageUser(null);
    Sentry.setUser(null);
  };
  const {
    current
  } = useRef({
    //* 서버 로그아웃도 화면 이동도 하지 않는다. 비로그인 가입 중에도 화면마다 실행되므로 가입 세션 키를 남긴다
    clearSession: ({
      toastOptions
    }: Pick<LogoutOptions, 'toastOptions'> = {}) => {
      clearStoredSession();
      stashLogoutToast(toastOptions);
      clearCredentials();
    },
    logout: async ({
      redirect,
      toastOptions: toastOptions_0
    }: LogoutOptions = {}) => {
      clearStoredSession();
      SESSION_KEYS_CLEARED_ON_USER_LOGOUT.forEach(key => sessionStorage.removeItem(key));
      stashLogoutToast(toastOptions_0);
      setIsDisabledAccessControl(true);
      try {
        await AuthApi['post:/wello/v2/auth/member-oauth/logout']();
      } catch {
        // 실패해도 클라이언트 로그아웃 진행
      }
      clearCredentials();

      //* KB Pay 웹뷰는 우리가 화면을 옮기지 않는다 · 제휴사 앱 전환 중 이동은 스킴 발사를 취소한다
      const isNavigationBlocked = getSubDomain() === SUB_DOMAIN.KB_PAY || !isAppLaunchSettled();
      if (isNavigationBlocked) {
        //* 이동이 컨텍스트를 날려주는 데 기대지 않는다 — 안 옮기면 진행 표시를 직접 되돌려야
        //* 비로그인인데 인증 게이트가 열린 채로 남지 않는다
        setIsDisabledAccessControl(false);
        return;
      }
      if (redirect) {
        return location.replace(redirect);
      }
      return location.reload();
    }
  });

  // eslint-disable-next-line react-hooks/refs -- 렌더마다 stable한 logout 함수 객체를 반환하기 위해 ref.current를 반환하는 의도적 패턴
  return current;
};